import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

import { LorenColor } from "./constants/loren-color.constants";
import { LorenInputTypes } from "./constants/loren-input-types.constants";
import { LorenFormField } from "../../../loren-entity-form-card/interfaces/loren-form-field.interface";
import { LorenFieldTypes } from "../../../loren-entity-form-card/enums/loren-field-types.enum";
import { FormFieldInteraction } from "../../../loren-entity-form-card/components/loren-entity-form-card/interfaces/form-field-interaction.interface";
import { LorenMeteringType } from "../../../../../features/loren-assets/interfaces/loren-metering-type";
import {CdkDragDrop, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";

@Component({
    selector: "loren-entity-form-field",
    templateUrl: "./loren-entity-form-field.component.html",
    styleUrls: ["./loren-entity-form-field.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenEntityFormFieldComponent implements OnInit {
  @Input() look = "col";
  @Input() isHandset: boolean;
  @Input() field: LorenFormField;
  @Input() fullLength: boolean;
  @Input() formGroup: UntypedFormGroup;

  @Output() formFieldInteraction = new EventEmitter<FormFieldInteraction>();
  formFieldTypes = LorenFieldTypes;
  colors = LorenColor;
  hidePassword = true;
  passwordField: boolean;
  selectedSensors: LorenMeteringType[] = [];
  availableSensors: LorenMeteringType[] = [];

  ngOnInit(): void {
    this.passwordField =
      this.field.formControlType === LorenFieldTypes.INPUT &&
      this.field.inputType === LorenInputTypes.PASSWORD;
  }

  resolveInputType() {
    if (this.passwordField) {
      return this.hidePassword
        ? LorenInputTypes.PASSWORD
        : LorenInputTypes.TEXT;
    }

    return this.field.inputType;
  }

  hasError(controlName: string, errorName: string): boolean {
    return this.formGroup.controls[controlName].hasError(errorName);
  }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleFormFieldInteraction($event: any, field: LorenFormField) {
    const result: FormFieldInteraction = {
      fieldName: field.name,
    };

    switch (field.formControlType) {
      case LorenFieldTypes.PERIOD:
        result.fieldCurrentValue = $event.days;
        break;
      case LorenFieldTypes.TEXTAREA:
      case LorenFieldTypes.INPUT:
        field.value = $event.currentTarget.value;
        result.fieldCurrentValue = $event.currentTarget.value;
        break;
      case LorenFieldTypes.DATETIME:
        result.fieldCurrentValue = $event.value;
        break;
      case LorenFieldTypes.SELECT:
      case LorenFieldTypes.COLOR:
      case LorenFieldTypes.CALENDAR:
        field.value = $event;
        result.fieldCurrentValue = $event;
        break;
      case LorenFieldTypes.AUTOCOMPLETE:
        result.fieldCurrentValue = $event;
        break;
      case LorenFieldTypes.SLIDE_TOGGLE:
        result.fieldCurrentValue = $event.checked;
        break;
      case LorenFieldTypes.CHECKBOX:
        result.fieldCurrentValue = $event.checked;
        break;
    }

    this.formFieldInteraction.emit(result);
  }

  handleAutoCompleteFiltering(field: LorenFormField) {
    field.filteredOptions = field.options.filter((x) =>
      x.key
        .toLowerCase()
        .includes(this.formGroup.controls[field.name].value?.toLowerCase())
    );

    if (field.filteredOptions.length === 0) {
      field.filteredOptions = field.options;
    }
  }

  handleAutoCompleteValueSelection(
    $event: MatAutocompleteSelectedEvent,
    field: LorenFormField
  ) {
    const option = field.options.find(
      (x) => x.value[field.transformByProperty] === $event.option?.value
    );
    field.transformedValue = option ? option.value : null;
    this.handleFormFieldInteraction(
      field.transformedValue ?? $event.option?.value,
      field
    );
  }

  isCalendarSelectedDate = ($event) => {
    return this.field.value.toDate().getTime() === $event.toDate().getTime()
      ? "calendar-selected-date"
      : null;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectCalendarDate(event: any, calendar: any) {
    this.handleFormFieldInteraction(event, this.field);
    calendar.updateTodaysDate();
  }

  onDrop(event: CdkDragDrop<LorenMeteringType[]>) {
      if (event.previousContainer === event.container) {
            moveItemInArray(
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
      } else {
          transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
          );
      }

      this.selectedSensors = event.container.id === 'selectedList' ? [...event.container.data] : [...this.selectedSensors];
      this.availableSensors = event.container.id === 'availableList' ? [...event.container.data] : [...this.availableSensors];
      this.updateFormGroup();
    }

    removeSensor(sensor: LorenMeteringType) {
        this.selectedSensors = this.selectedSensors.filter((s) => s !== sensor);
        this.availableSensors = [...this.availableSensors, sensor];
        this.updateFormGroup();
    }

    addSensor(sensor: LorenMeteringType) {
        this.availableSensors = this.availableSensors.filter((s) => s !== sensor);
        this.selectedSensors = [...this.selectedSensors, sensor];
        this.updateFormGroup();
    }

    private updateFormGroup() {
        this.formGroup.controls[this.field.name].setValue({
            selectedSensors: this.selectedSensors,
            availableSensors: this.availableSensors,
        });

        this.handleFormFieldInteraction(
            {
                selectedSensors: this.selectedSensors,
                availableSensors: this.availableSensors,
            },
            this.field
        );
    }
}
